import { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/Layout/AdminLayout';
import { api } from '../../services/api';
import type { DeclarationConfig, DeclarationConfigCreateRequest } from '@vedjegy/shared';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type EditRow = DeclarationConfig & { dirty: boolean; saving: boolean };

function toEditRow(d: DeclarationConfig): EditRow {
  return { ...d, dirty: false, saving: false };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AdminDeclarationConfigPage() {
  const [rows, setRows]               = useState<EditRow[]>([]);
  const [loading, setLoading]         = useState(true);
  const [globalError, setGlobalError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newRow, setNewRow]           = useState<DeclarationConfigCreateRequest>({
    declarationKey: '', groupType: 'exclusion', label: '', isExclusionFactor: false,
  });
  const [addError, setAddError]   = useState('');
  const [addSaving, setAddSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.declarationConfig.list();
      setRows(data.declarations.map(toEditRow));
    } catch {
      setGlobalError('Nem sikerült betölteni a nyilatkozatokat.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  function updateField(id: string, field: keyof EditRow, value: unknown) {
    setRows((prev) => prev.map((r) => r.id === id ? { ...r, [field]: value, dirty: true } : r));
  }

  async function saveRow(row: EditRow) {
    setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, saving: true } : r));
    try {
      await api.declarationConfig.update(row.id, {
        label:              row.label,
        isExclusionFactor:  row.isExclusionFactor,
        displayOrder:       row.displayOrder,
        isActive:           row.isActive,
      });
      setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, dirty: false, saving: false } : r));
    } catch {
      setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, saving: false } : r));
      setGlobalError('Mentés sikertelen: ' + row.declarationKey);
    }
  }

  async function deleteRow(row: EditRow) {
    if (!confirm(`Törli a(z) "${row.declarationKey}" nyilatkozatot? Ez visszavonhatatlan.`)) return;
    try {
      await api.declarationConfig.delete(row.id);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch {
      setGlobalError('Törlés sikertelen.');
    }
  }

  async function handleAdd() {
    setAddError('');
    if (!newRow.declarationKey.trim() || !newRow.label.trim()) {
      setAddError('Kulcs és szöveg kötelező.'); return;
    }
    setAddSaving(true);
    try {
      await api.declarationConfig.create(newRow);
      setShowAddForm(false);
      setNewRow({ declarationKey: '', groupType: 'exclusion', label: '', isExclusionFactor: false });
      await load();
    } catch (e: unknown) {
      const msg = (e as { message?: string })?.message ?? 'Hiba';
      setAddError(msg.includes('409') ? 'Ez a kulcs már létezik.' : 'Hozzáadás sikertelen.');
    } finally {
      setAddSaving(false);
    }
  }

  // ---- styles ----
  const th: React.CSSProperties = {
    textAlign: 'left', padding: '8px 10px', fontSize: 12,
    fontWeight: 700, color: '#6b7280', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap',
  };
  const td: React.CSSProperties = { padding: '6px 8px', verticalAlign: 'middle' };
  const inp = (w?: number): React.CSSProperties => ({
    width: w ?? '100%', padding: '5px 8px', border: '1px solid #d1d5db',
    borderRadius: 5, fontSize: 13, boxSizing: 'border-box',
  });

  const exclusionRows  = rows.filter((r) => r.groupType === 'exclusion');
  const submissionRows = rows.filter((r) => r.groupType === 'submission');

  function renderTable(group: EditRow[], groupLabel: string) {
    return (
      <div style={{ marginBottom: 32 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>
          {groupLabel}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                <th style={{ ...th, minWidth: 160 }}>Kulcs</th>
                <th style={{ ...th, minWidth: 300 }}>Szöveg</th>
                <th style={th}>Kizáró tény.</th>
                <th style={th}>Sorrend</th>
                <th style={th}>Aktív</th>
                <th style={th}>Műveletek</th>
              </tr>
            </thead>
            <tbody>
              {group.map((row) => (
                <tr key={row.id} style={{ borderBottom: '1px solid #f3f4f6', background: row.dirty ? '#fffbeb' : '#fff' }}>
                  <td style={{ ...td, color: '#6b7280', fontWeight: 600, fontFamily: 'monospace', fontSize: 12 }}>{row.declarationKey}</td>
                  <td style={td}>
                    <textarea spellCheck={true} lang="hu"
                      style={{ ...inp(), height: 60, resize: 'vertical', fontFamily: 'inherit' }}
                      value={row.label}
                      onChange={(e) => updateField(row.id, 'label', e.target.value)}
                    />
                  </td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    {row.groupType === 'exclusion' && (
                      <input type="checkbox" checked={row.isExclusionFactor}
                        onChange={(e) => updateField(row.id, 'isExclusionFactor', e.target.checked)} />
                    )}
                  </td>
                  <td style={td}>
                    <input style={inp(60)} type="number" min={0} value={row.displayOrder}
                      onChange={(e) => updateField(row.id, 'displayOrder', parseInt(e.target.value) || 0)} />
                  </td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    <input type="checkbox" checked={row.isActive}
                      onChange={(e) => updateField(row.id, 'isActive', e.target.checked)} />
                  </td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => void saveRow(row)}
                        disabled={!row.dirty || row.saving}
                        style={{
                          padding: '4px 12px', fontSize: 12, fontWeight: 600,
                          background: row.dirty ? '#0ea5e9' : '#e5e7eb',
                          color: row.dirty ? '#fff' : '#9ca3af',
                          border: 'none', borderRadius: 5,
                          cursor: row.dirty ? 'pointer' : 'default',
                        }}
                      >
                        {row.saving ? '…' : 'Mentés'}
                      </button>
                      <button
                        onClick={() => void deleteRow(row)}
                        style={{ padding: '4px 10px', fontSize: 12, fontWeight: 600, background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 5, cursor: 'pointer' }}
                      >
                        Törlés
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {group.length === 0 && (
                <tr><td colSpan={6} style={{ padding: '12px 10px', color: '#9ca3af', fontSize: 13 }}>Nincs nyilatkozat ebben a csoportban.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <AdminLayout>
      <div style={{ padding: 28, maxWidth: 2000 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Nyilatkozat konfiguráció</h1>
          <button
            onClick={() => { setShowAddForm((v) => !v); setAddError(''); }}
            style={{ padding: '8px 20px', background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
          >
            {showAddForm ? 'Mégse' : '+ Új nyilatkozat'}
          </button>
        </div>

        {globalError && (
          <div style={{ marginBottom: 16, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, color: '#dc2626', fontSize: 13 }}>
            {globalError}
            <button onClick={() => setGlobalError('')} style={{ marginLeft: 10, fontSize: 11, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
          </div>
        )}

        {/* Add form */}
        {showAddForm && (
          <div style={{ marginBottom: 24, padding: 16, background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Új nyilatkozat</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ minWidth: 160 }}>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 3 }}>Kulcs *</div>
                <input style={inp()} placeholder="pl. new_declaration" value={newRow.declarationKey}
                  onChange={(e) => setNewRow((p) => ({ ...p, declarationKey: e.target.value }))} />
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 3 }}>Csoport *</div>
                <select style={inp(140)} value={newRow.groupType}
                  onChange={(e) => setNewRow((p) => ({ ...p, groupType: e.target.value as 'exclusion' | 'submission' }))}>
                  <option value="exclusion">Kizáró tényező</option>
                  <option value="submission">Alávetési nyilatkozat</option>
                </select>
              </div>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 3 }}>Szöveg *</div>
                <textarea spellCheck={true} lang="hu" style={{ ...inp(), height: 52, resize: 'vertical', fontFamily: 'inherit' }}
                  placeholder="Nyilatkozat szövege" value={newRow.label}
                  onChange={(e) => setNewRow((p) => ({ ...p, label: e.target.value }))} />
              </div>
              {newRow.groupType === 'exclusion' && (
                <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', paddingBottom: 6 }}>
                  <input type="checkbox" checked={newRow.isExclusionFactor ?? false}
                    onChange={(e) => setNewRow((p) => ({ ...p, isExclusionFactor: e.target.checked }))} />
                  Kizáró tényező
                </label>
              )}
              <button
                onClick={() => void handleAdd()}
                disabled={addSaving}
                style={{ padding: '8px 20px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: 'pointer', alignSelf: 'flex-end' }}
              >
                {addSaving ? 'Mentés…' : 'Hozzáadás'}
              </button>
            </div>
            {addError && <div style={{ marginTop: 8, fontSize: 13, color: '#dc2626' }}>{addError}</div>}
          </div>
        )}

        {loading ? (
          <div style={{ color: '#9ca3af', fontSize: 14 }}>Betöltés…</div>
        ) : (
          <>
            {renderTable(exclusionRows,  'Kizáró tényezők')}
            {renderTable(submissionRows, 'Alávetési nyilatkozatok')}
          </>
        )}

        <p style={{ marginTop: 8, fontSize: 12, color: '#9ca3af' }}>
          A módosítások azonnal érvénybe lépnek — a pályázati form ezeket a nyilatkozatokat fogja megjeleníteni.
        </p>
      </div>
    </AdminLayout>
  );
}
